import React, { useState, useEffect } from 'react';
import { deviceAPI, settingsAPI } from '../services/api';

const DeviceRegistration = () => {
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [registeredTeamIds, setRegisteredTeamIds] = useState([]);
  const [deliveryUnits, setDeliveryUnits] = useState([]);
  const [formData, setFormData] = useState({
    assetId: '',
    deviceName: '',
    deviceType: '',
    model: '',
    osVersion: '',
    color: '',
    ram: '',
    displaySize: '',
    storage: '',
    chipset: '',
    udid: '',
    serialNumber: '',
    arSupport: 'Not Available',
    screenResolution: '',
    condition: 'GOOD',
    purchaseDate: '',
    purchasedFrom: '',
    purchaseStatus: '',
    registeredTeamId: '',
    usageType: 'Testing',
    description: '',
    deliveryUnit: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadDeviceTypes();
    loadRegisteredTeamIds();
    loadDeliveryUnits();
  }, []);

  // Auto-fill NA for RAM and Screen Resolution for certain device types
  useEffect(() => {
    const deviceTypesToFillNA = ['Instax Printer', 'Instax Camera', 'Digital Camera'];
    
    if (deviceTypesToFillNA.includes(formData.deviceType)) {
      setFormData(prev => ({
        ...prev,
        ram: 'NA',
        screenResolution: 'NA'
      }));
    }
  }, [formData.deviceType]);

  const loadDeviceTypes = async () => {
    try {
      const response = await settingsAPI.getDeviceTypes();
      const types = Array.isArray(response.data) ? response.data : (response.data?.deviceTypes || []);
      setDeviceTypes(types);
      if (types.length > 0 && !formData.deviceType) {
        setFormData(prev => ({ ...prev, deviceType: types[0] }));
      }
    } catch (error) {
      console.error('Failed to load device types:', error);
      const defaultTypes = ['iPhone', 'Android Phone', 'iPad', 'Tablet', 'Laptop', 'Printer', 'Camera', 'Other'];
      setDeviceTypes(defaultTypes);
      if (!formData.deviceType) {
        setFormData(prev => ({ ...prev, deviceType: defaultTypes[0] }));
      }
      setMessage({
        type: 'error',
        text: 'Failed to load device types from server. Using default types.'
      });
    }
  };

  const loadRegisteredTeamIds = async () => {
    try {
      const response = await settingsAPI.getRegisteredTeamIds();
      const teamIds = Array.isArray(response.data) ? response.data : (response.data?.registeredTeamIds || []);
      setRegisteredTeamIds(teamIds);
    } catch (error) {
      console.error('Failed to load registered team IDs:', error);
      setRegisteredTeamIds([]);
    }
  };

  const loadDeliveryUnits = async () => {
    try {
      const response = await settingsAPI.getDeliveryUnits();
      const units = Array.isArray(response.data) ? response.data : (response.data?.deliveryUnits || []);
      setDeliveryUnits(units);
    } catch (error) {
      console.error('Failed to load delivery units:', error);
      const defaultUnits = ['DU1', 'DU2', 'DU3', 'DU4', 'DU5'];
      setDeliveryUnits(defaultUnits);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await deviceAPI.create(formData);
      setMessage({ type: 'success', text: 'Device registered successfully!' });
      setFormData({
        assetId: '',
        deviceName: '',
        deviceType: deviceTypes.length > 0 ? deviceTypes[0] : '',
        model: '',
        osVersion: '',
        color: '',
        ram: '',
        displaySize: '',
        storage: '',
        chipset: '',
        udid: '',
        serialNumber: '',
        arSupport: 'Not Available',
        screenResolution: '',
        condition: 'GOOD',
        purchaseDate: '',
        purchasedFrom: '',
        purchaseStatus: '',
        registeredTeamId: '',
        usageType: 'Testing',
        description: '',
        deliveryUnit: ''
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to register device'
      });
    } finally {
      setLoading(false);
    }
  };

  const fillTestData = () => {
    const testData = {
      assetId: 'DF-001',
      deviceName: 'iPhone 15 Pro',
      deviceType: deviceTypes.length > 0 ? deviceTypes[0] : 'iPhone',
      model: 'iPhone 15 Pro',
      osVersion: 'iOS 17.2',
      color: 'Natural Titanium',
      ram: '8GB',
      displaySize: '6.1 inches',
      storage: '256GB',
      chipset: 'A17 Pro',
      udid: 'TEST-UDID-001',
      serialNumber: 'SN123456789',
      arSupport: 'Available',
      screenResolution: '2556 x 1179',
      condition: 'GOOD',
      purchaseDate: '2024-01-15',
      purchasedFrom: 'Apple Store',
      purchaseStatus: 'New',
      registeredTeamId: registeredTeamIds.length > 0 ? registeredTeamIds[0] : '',
      usageType: 'Development',
      deliveryUnit: deliveryUnits.length > 0 ? deliveryUnits[0] : '',
      description: 'Latest iPhone model with Pro features'
    };
    
    setFormData(testData);
    setMessage({ type: 'info', text: 'Form filled with test data. You can modify and register manually.' });
  };

  return (
    <div className="card">
      <h2 style={{ marginBottom: '0.75rem' }}>Register New Device</h2>
      
      {message.text && (
        <div className={`alert alert-${message.type === 'info' ? 'success' : message.type}`} style={{ marginBottom: '0.75rem' }}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Basic Information Section */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(4, 1fr)', 
          gap: '0.75rem',
          marginBottom: '0.75rem'
        }}>
          <div className="form-group" style={{ marginBottom: '0.5rem' }}>
            <label htmlFor="deliveryUnit" style={{ marginBottom: '0.25rem' }}>Delivery Unit *</label>
            <select
              id="deliveryUnit"
              name="deliveryUnit"
              value={formData.deliveryUnit}
              onChange={handleChange}
              required
            >
              <option value="">Select Delivery Unit</option>
              {deliveryUnits.map((unit, index) => (
                <option key={index} value={unit}>{unit}</option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: '0.5rem' }}>
            <label htmlFor="deviceType" style={{ marginBottom: '0.25rem' }}>Device Type *</label>
            <select
              id="deviceType"
              name="deviceType"
              value={formData.deviceType}
              onChange={handleChange}
              required
            >
              <option value="">Select type</option>
              {deviceTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: '0.5rem' }}>
            <label htmlFor="deviceName" style={{ marginBottom: '0.25rem' }}>Device Name *</label>
            <input
              type="text"
              id="deviceName"
              name="deviceName"
              value={formData.deviceName}
              onChange={handleChange}
              required
              placeholder="iPhone 14 Pro"
            />
          </div>

          <div className="form-group" style={{ marginBottom: '0.5rem' }}>
            <label htmlFor="assetId" style={{ marginBottom: '0.25rem' }}>Asset ID *</label>
            <input
              type="text"
              id="assetId"
              name="assetId"
              value={formData.assetId}
              onChange={handleChange}
              required
              placeholder="ASSET-001"
            />
          </div>

          <div className="form-group" style={{ marginBottom: '0.5rem' }}>
            <label htmlFor="model" style={{ marginBottom: '0.25rem' }}>Model *</label>
            <input
              type="text"
              id="model"
              name="model"
              value={formData.model}
              onChange={handleChange}
              required
              placeholder="iPhone 14 Pro Max, Link4 P0"
            />
          </div>

          <div className="form-group" style={{ marginBottom: '0.5rem' }}>
            <label htmlFor="osVersion" style={{ marginBottom: '0.25rem' }}>OS Version</label>
            <input
              type="text"
              id="osVersion"
              name="osVersion"
              value={formData.osVersion}
              onChange={handleChange}
              placeholder="iOS 17.0"
            />
          </div>

          <div className="form-group" style={{ marginBottom: '0.5rem' }}>
            <label htmlFor="color" style={{ marginBottom: '0.25rem' }}>Color</label>
            <input
              type="text"
              id="color"
              name="color"
              value={formData.color}
              onChange={handleChange}
              placeholder="Space Black"
            />
          </div>

          <div className="form-group" style={{ marginBottom: '0.5rem' }}>
            <label htmlFor="ram" style={{ marginBottom: '0.25rem' }}>RAM</label>
            <input
              type="text"
              id="ram"
              name="ram"
              value={formData.ram}
              onChange={handleChange}
              placeholder={['Instax Printer', 'Instax Camera', 'Digital Camera'].includes(formData.deviceType) ? 'NA' : '8GB'}
            />
          </div>

          <div className="form-group" style={{ marginBottom: '0.5rem' }}>
            <label htmlFor="displaySize" style={{ marginBottom: '0.25rem' }}>Display Size</label>
            <input
              type="text"
              id="displaySize"
              name="displaySize"
              value={formData.displaySize}
              onChange={handleChange}
              placeholder="6.1 inches"
            />
          </div>

          <div className="form-group" style={{ marginBottom: '0.5rem' }}>
            <label htmlFor="storage" style={{ marginBottom: '0.25rem' }}>Storage</label>
            <input
              type="text"
              id="storage"
              name="storage"
              value={formData.storage}
              onChange={handleChange}
              placeholder="256GB"
            />
          </div>

          <div className="form-group" style={{ marginBottom: '0.5rem' }}>
            <label htmlFor="chipset" style={{ marginBottom: '0.25rem' }}>Chipset</label>
            <input
              type="text"
              id="chipset"
              name="chipset"
              value={formData.chipset}
              onChange={handleChange}
              placeholder="A17 Pro"
            />
          </div>

          <div className="form-group" style={{ marginBottom: '0.5rem' }}>
            <label htmlFor="screenResolution" style={{ marginBottom: '0.25rem' }}>Screen Resolution</label>
            <input
              type="text"
              id="screenResolution"
              name="screenResolution"
              value={formData.screenResolution}
              onChange={handleChange}
              placeholder={['Instax Printer', 'Instax Camera', 'Digital Camera'].includes(formData.deviceType) ? 'NA' : '2556x1179'}
            />
          </div>

          <div className="form-group" style={{ marginBottom: '0.5rem' }}>
            <label htmlFor="udid" style={{ marginBottom: '0.25rem' }}>Unique Device ID (Optional)</label>
            <input
              type="text"
              id="udid"
              name="udid"
              value={formData.udid}
              onChange={handleChange}
              placeholder="00000000-0000000000"
            />
          </div>

          <div className="form-group" style={{ marginBottom: '0.5rem' }}>
            <label htmlFor="serialNumber" style={{ marginBottom: '0.25rem' }}>Serial Number (Optional)</label>
            <input
              type="text"
              id="serialNumber"
              name="serialNumber"
              value={formData.serialNumber}
              onChange={handleChange}
              placeholder="Device Serial Number"
            />
          </div>
        </div>

        {/* Purchase Information Section */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(4, 1fr)', 
          gap: '0.75rem',
          marginBottom: '0.75rem',
          paddingTop: '0.5rem',
          borderTop: '1px solid #e0e4e7'
        }}>
          <div className="form-group" style={{ marginBottom: '0.5rem' }}>
            <label htmlFor="purchaseDate" style={{ marginBottom: '0.25rem' }}>Purchase Date</label>
            <input
              type="date"
              id="purchaseDate"
              name="purchaseDate"
              value={formData.purchaseDate}
              onChange={handleChange}
            />
          </div>

          <div className="form-group" style={{ marginBottom: '0.5rem' }}>
            <label htmlFor="purchasedFrom" style={{ marginBottom: '0.25rem' }}>Purchased From</label>
            <input
              type="text"
              id="purchasedFrom"
              name="purchasedFrom"
              value={formData.purchasedFrom}
              onChange={handleChange}
              placeholder="Store/Vendor name"
            />
          </div>

          <div className="form-group" style={{ marginBottom: '0.5rem' }}>
            <label htmlFor="purchaseStatus" style={{ marginBottom: '0.25rem' }}>Purchase Status</label>
            <select
              id="purchaseStatus"
              name="purchaseStatus"
              value={formData.purchaseStatus}
              onChange={handleChange}
            >
              <option value="">Select status</option>
              <option value="New">New</option>
              <option value="Used">Used</option>
              <option value="CustomerSupplied">CustomerSupplied</option>
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: '0.5rem' }}>
            <label htmlFor="registeredTeamId" style={{ marginBottom: '0.25rem' }}>Registered Team ID</label>
            <select
              id="registeredTeamId"
              name="registeredTeamId"
              value={formData.registeredTeamId}
              onChange={handleChange}
            >
              <option value="">-- Select Team ID --</option>
              {registeredTeamIds.map((teamId, index) => (
                <option key={index} value={teamId}>{teamId}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Condition and Other Fields Section */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(4, 1fr)', 
          gap: '0.75rem',
          marginBottom: '0.75rem',
          paddingTop: '0.5rem',
          borderTop: '1px solid #e0e4e7'
        }}>
          <div className="form-group" style={{ marginBottom: '0.5rem' }}>
            <label htmlFor="condition" style={{ marginBottom: '0.25rem' }}>Condition *</label>
            <select
              id="condition"
              name="condition"
              value={formData.condition}
              onChange={handleChange}
              required
            >
              <option value="GOOD">GOOD</option>
              <option value="BAD">BAD</option>
              <option value="Retired">Retired</option>
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: '0.5rem' }}>
            <label htmlFor="arSupport" style={{ marginBottom: '0.25rem' }}>AR Support</label>
            <select
              id="arSupport"
              name="arSupport"
              value={formData.arSupport}
              onChange={handleChange}
            >
              <option value="Not Available">Not Available</option>
              <option value="Available">Available</option>
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: '0.5rem' }}>
            <label htmlFor="usageType" style={{ marginBottom: '0.25rem' }}>Usage Type</label>
            <select
              id="usageType"
              name="usageType"
              value={formData.usageType}
              onChange={handleChange}
            >
              <option value="">Select usage type</option>
              <option value="Development">Development</option>
              <option value="Testing">Testing</option>
            </select>
          </div>

          <div></div>
        </div>

        {/* Description Section */}
        <div className="form-group" style={{ marginBottom: '0.75rem' }}>
          <label htmlFor="description" style={{ marginBottom: '0.25rem' }}>Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Additional notes or description"
            rows="2"
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem',
              background: 'var(--light-surface)',
              border: '1px solid rgba(0, 0, 0, 0.2)',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.875rem',
              color: 'var(--light-text)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              fontWeight: '500',
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Registering...' : 'Register Device'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DeviceRegistration;
